import React, { useCallback, useMemo, useState } from 'react'
import { Tabs } from 'antd'
import { getPermissions } from 'src/utils/Common'
import { permitsList } from 'src/constants/permits'
import TradeTab from './TradeTab'
import BackComercialTab from './BackComercialTab'
import SyncTab from './SyncTab'
import CanalTab from './CanalTab'
import PromosTab from './PromosTab'
import AuditTab from './AuditTab'
import VtexTab from './VtexTab'
import ClasificacionesTab from './ClasificacionesTab'
import MargenesTab from './MargenesTab'
import ResumenBanner from './ResumenBanner'
import PreciosStyles from './PreciosStyles'

const isoLocal = (d) => {
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}
const _hoy = new Date()
const DEF_DESDE = isoLocal(new Date(_hoy.getFullYear(), _hoy.getMonth(), 1))
const DEF_HASTA = isoLocal(_hoy)

// TEMP: mientras se valida el motor de precios, se muestran solo estos tabs. null = mostrar todos.
// Quitar (o poner null) para volver a ver IA Victory / IA TVS / Impulsa / Vtex / Auditoría.
const TABS_VISIBLES = ['trade', 'back', 'promos', 'clasificaciones', 'margenes', 'sync', 'vtex']

const PreciosContainer = () => {
  const permisos = getPermissions() || {}
  const has = (id) => Boolean(permisos[id])
  const isAdmin = has(permitsList.PRECIOS_ADMIN)
  const [active, setActive] = useState('')
  // El periodo ya no se elige: quedaba fijo en el mes corriente y nadie lo movia. Se conserva como
  // valor para Trade, Auditoria y los KPIs de Cambios/Envios, que lo siguen recibiendo igual.
  const desde = DEF_DESDE
  const hasta = DEF_HASTA
  // señal de refresco: cualquier mutación en un tab la incrementa -> ResumenBanner (KPIs+alertas) re-consulta
  const [tick, setTick] = useState(0)
  const onChanged = useCallback(() => setTick((t) => t + 1), [])

  const items = useMemo(() => {
    const tabs = []
    // Orden = flujo secuencial: 1 Trade (precio) -> 2 Promos (bonos) -> 3 Back (plano) -> 4 downstream (IA/Impulsa/Vtex) -> transversal (Sync/Auditoría)
    // 1. Precio
    if (has(permitsList.PRECIOS_TRADE) || isAdmin) {
      tabs.push({ key: 'trade', label: '1. Precios', children: <TradeTab onChanged={onChanged} /> })
    }
    // 2. Plano — un solo tab: adentro va la cascada completa (pendiente -> calcular -> completar -> publicar)
    if (has(permitsList.PRECIOS_BACKCOMERCIAL) || isAdmin) {
      tabs.push({ key: 'back', label: '2. Back Comercial', children: <BackComercialTab onChanged={onChanged} /> })
    }
    // 3. Bonos: SOLO quien tiene el permiso de Bonos y promociones (PRECIOS_PROMOS, 96). Desde 2026-10-08 ni
    // Admin ni Back Comercial la ven por defecto (el back también exige el 96 en todos sus endpoints).
    if (has(permitsList.PRECIOS_PROMOS)) {
      tabs.push({
        key: 'promos',
        label: '3. Bonos y promociones',
        children: <PromosTab onChanged={onChanged} />,
      })
    }
    // 4. Downstream (reflejan y confirman)
    if (has(permitsList.PRECIOS_IA_VICTORY) || isAdmin) {
      tabs.push({
        key: 'ia_victory',
        label: '4. IA Victory',
        children: (
          <CanalTab
            sistema="ia"
            grupo="victory"
            titulo="Actualización del agente de IA — Victory"
            descripcion="Marcas Victory, Kymco, Kawasaki, Ceronte. Confirma cuándo el PVP y bono quedaron reflejados en el agente."
            onChanged={onChanged}
          />
        ),
      })
    }
    if (has(permitsList.PRECIOS_IA_TVS) || isAdmin) {
      tabs.push({
        key: 'ia_tvs',
        label: '4. IA TVS',
        children: (
          <CanalTab
            sistema="ia"
            grupo="tvs"
            titulo="Actualización del agente de IA — TVS"
            descripcion="Confirma cuándo el PVP y bono quedaron reflejados en el agente de TVS."
            onChanged={onChanged}
          />
        ),
      })
    }
    if (has(permitsList.PRECIOS_IMPULSA) || isAdmin) {
      tabs.push({
        key: 'impulsa',
        label: '4. Impulsa',
        children: (
          <CanalTab
            sistema="impulsa"
            grupo="all"
            titulo="Actualización en Impulsa"
            descripcion="Precio base y bonos por zona/ciudad/modelo. Marca cuándo quedó actualizado en Impulsa."
            onChanged={onChanged}
          />
        ),
      })
    }
    // 4. Ecommerce Vtex (downstream, admin)
    if (isAdmin) {
      tabs.push({ key: 'vtex', label: '4. Ecommerce (Vtex)', children: <VtexTab onChanged={onChanged} /> })
    }
    // Transversal. "Clasificación de clientes" refundió 3 tabs (Tipos de lista + Clientes + la edición
    // de márgenes): una clasificación se configura completa en un solo lugar — su llave SAP, su fórmula,
    // su margen y sus socios. "Márgenes motocicletas" queda aparte porque responde otra pregunta: leer una
    // misma moto ACROSS todas las clasificaciones (la matriz de la hoja PRECIOS del Excel).
    if (has(permitsList.PRECIOS) || isAdmin) {
      const puedeConfigurar = has(permitsList.PRECIOS_TRADE) || has(permitsList.PRECIOS_BACKCOMERCIAL) || isAdmin
      tabs.push({
        key: 'clasificaciones',
        label: 'Reglas de carga de planos',
        children: <ClasificacionesTab puedeEditar={puedeConfigurar} onChanged={onChanged} />,
      })
      tabs.push({ key: 'margenes', label: 'Márgenes motocicletas', children: <MargenesTab /> })
      tabs.push({ key: 'sync', label: 'Sincronización', children: <SyncTab onChanged={onChanged} /> })
    }
    if (isAdmin) {
      tabs.push({ key: 'audit', label: 'Auditoría', children: <AuditTab desde={desde} hasta={hasta} /> })
    }
    return TABS_VISIBLES ? tabs.filter((t) => TABS_VISIBLES.includes(t.key)) : tabs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta, onChanged])

  return (
    <div className="precios-scope" style={{ padding: '18px 24px' }}>
      <PreciosStyles />

      {/* El banner con el título y el pipeline 1→2→3→4 se quitó: los tabs ya van numerados y decían lo mismo */}
      <ResumenBanner
        refreshKey={`${active}:${tick}`}
        desde={desde}
        hasta={hasta}
        onIr={(key) => { if (items.some((i) => i.key === key)) setActive(key) }}
      />
      {items.length ? (
        <Tabs activeKey={active || items[0].key} onChange={setActive} items={items} destroyInactiveTabPane />
      ) : (
        <p>No tienes permisos para ver este módulo.</p>
      )}
    </div>
  )
}

export default PreciosContainer
